import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Grid, Button, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AppsIcon from "@mui/icons-material/Apps";
import DnsIcon from "@mui/icons-material/Dns";
import TerminalIcon from "@mui/icons-material/Terminal";
import SecurityIcon from "@mui/icons-material/Security";
import TimerIcon from "@mui/icons-material/Timer";
import StorageIcon from "@mui/icons-material/Storage";
import CodeIcon from "@mui/icons-material/Code";
import { useAppSelector } from "../store";

interface PreviewCard {
  title: string;
  description: string;
  icon: React.ReactElement;
  tags: string[];
  visual: React.ReactNode;
}

const PREVIEW_CARDS: PreviewCard[] = [
  {
    title: "Service Catalog",
    description:
      "Browse 40+ pre-configured Docker services including databases, monitoring tools, CI/CD platforms, and more. Each service shows its description, default version, and pricing.",
    icon: <AppsIcon sx={{ fontSize: 40 }} />,
    tags: ["PostgreSQL", "Redis", "Grafana", "Jenkins", "MongoDB"],
    visual: (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
        {[
          "PostgreSQL",
          "Redis",
          "MongoDB",
          "Grafana",
          "Jenkins",
          "RabbitMQ",
          "Elasticsearch",
        ].map((s) => (
          <Chip key={s} label={s} size="small" variant="outlined" />
        ))}
        <Chip label="+33 more" size="small" color="primary" />
      </Box>
    ),
  },
  {
    title: "One-Click Deployment",
    description:
      "Select a service, choose your duration (1-72 hours), and deploy. Your instance is running in seconds with unique credentials and a dedicated port.",
    icon: <DnsIcon sx={{ fontSize: 40 }} />,
    tags: ["Instant", "Isolated", "Secure"],
    visual: (
      <Box sx={{ mt: 2, p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
          <StorageIcon sx={{ color: "#3b82f6" }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
            }}
          >
            postgres-af9e9627
          </Typography>
          <Chip label="Running" size="small" color="success" />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontFamily: "monospace",
          }}
        >
          78.47.65.185:7000
        </Typography>
      </Box>
    ),
  },
  {
    title: "Real-Time Dashboard",
    description:
      "Monitor all your active instances from a central dashboard. See status, time remaining, resource usage, and quick actions at a glance.",
    icon: <DashboardIcon sx={{ fontSize: 40 }} />,
    tags: ["Monitoring", "Status", "Overview"],
    visual: (
      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, p: 1.5, bgcolor: "#eff6ff", borderRadius: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "#1e40af",
              fontWeight: 600,
            }}
          >
            Active
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1d4ed8",
            }}
          >
            2
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "#166534",
              fontWeight: 600,
            }}
          >
            Time Left
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#16a34a",
            }}
          >
            57m
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    title: "Connection Details & Credentials",
    description:
      "Each instance gets unique credentials (username, password) and a direct connection endpoint. Copy connection strings, view the Docker Compose configuration, and access logs.",
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    tags: ["Credentials", "Connection", "Compose"],
    visual: (
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: "#f9fafb",
          borderRadius: 2,
          fontFamily: "monospace",
          fontSize: "0.8rem",
        }}
      >
        <Box>Host: 78.47.65.185:7001</Box>
        <Box>User: postgres</Box>
        <Box>Pass: ********</Box>
      </Box>
    ),
  },
  {
    title: "Live Logs & Docker Compose",
    description:
      "View real-time container logs directly from the UI. Inspect the Docker Compose YAML used to deploy your service, and use it as a template for your own projects.",
    icon: <TerminalIcon sx={{ fontSize: 40 }} />,
    tags: ["Logs", "Docker", "YAML"],
    visual: (
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: "#111827",
          color: "#d1d5db",
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: "0.75rem",
        }}
      >
        <Box>postgres | 2026-05-21 LOG: database system is ready</Box>
        <Box>postgres | 2026-05-21 LOG: listening on IPv4 0.0.0.0:5432</Box>
      </Box>
    ),
  },
  {
    title: "Time-Based Lifecycle",
    description:
      "Instances run for your selected duration, then expire automatically. Stop and resume at any time - the timer pauses while stopped. Restart or terminate with one click.",
    icon: <TimerIcon sx={{ fontSize: 40 }} />,
    tags: ["Stop", "Resume", "Auto-Expire"],
    visual: (
      <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
        {["Stop", "Restart", "Terminate"].map((action) => (
          <Chip
            key={action}
            label={action}
            size="small"
            variant="outlined"
            color={
              action === "Terminate"
                ? "error"
                : action === "Stop"
                  ? "warning"
                  : "primary"
            }
          />
        ))}
      </Box>
    ),
  },
  {
    title: "Developer-Friendly",
    description:
      "Use standard connection strings from any client, IDE, or application. Every service runs in an isolated Docker container with its own network and port allocation.",
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    tags: ["API", "Docker", "Isolation"],
    visual: (
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: "#111827",
          color: "#d1d5db",
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: "0.75rem",
        }}
      >
        <Box>$ psql -h 78.47.65.185 -p 7001 -U postgres</Box>
        <Box sx={{ color: "#10b981" }}>Password: ********</Box>
        <Box sx={{ color: "#10b981" }}>postgres=# \dt</Box>
      </Box>
    ),
  },
];

const PreviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 800,
          }}
        >
          See What LTS Can Do
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            maxWidth: 700,
            mx: "auto",
            mb: 3,
          }}
        >
          Explore how Pinehood LTS helps you deploy and manage Docker services
          instantly. Here is a preview of the key features.
        </Typography>
        {!isAuthenticated && (
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/login")}
            sx={{ px: 5, py: 1.5, fontWeight: 600 }}
          >
            {t("home.getStarted")}
          </Button>
        )}
      </Box>
      <Grid container spacing={3}>
        {PREVIEW_CARDS.map((card, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={card.title}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                transition: "box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      mt: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {card.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.7,
                }}
              >
                {card.description}
              </Typography>
              {card.visual}
            </Paper>
          </Grid>
        ))}
      </Grid>
      {!isAuthenticated && (
        <Box sx={{ textAlign: "center", mt: 6, mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
            }}
          >
            Ready to try it?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 3,
            }}
          >
            Sign in with Google and deploy your first service in under a minute.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/login")}
            sx={{ px: 5, py: 1.5, fontWeight: 600 }}
          >
            {t("home.getStarted")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PreviewPage;
